/**
 * Token Management Utilities
 *
 * Storage model:
 * - Access token (15 min lifetime): localStorage, sent as Authorization header.
 * - Refresh token: httpOnly cookie set by the backend — never readable from JS,
 *   never stored here. Refresh calls just hit the endpoint with credentials.
 * - Refresh expiry marker: a plain timestamp in localStorage used only to decide
 *   "is this session probably still alive?" client-side. It contains no secret.
 */

const ACCESS_TOKEN_KEY = 'welynk_access_token';
const TOKEN_EXPIRY_KEY = 'welynk_token_expiry';
const REFRESH_EXPIRY_KEY = 'welynk_refresh_expiry';

/** Pre-cookie deployments stored the refresh token itself here. */
const LEGACY_REFRESH_TOKEN_KEY = 'welynk_refresh_token';

/** Fallback only — used when a caller doesn't have refreshExpiresIn (e.g. legacy callers). */
const DEFAULT_REFRESH_LIFETIME_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Store the access token with expiry information and extend the session marker.
 *
 * `refreshExpiresIn` (seconds) should come from the login/refresh response body —
 * it reflects the *actual* refresh token lifetime, which varies with "remember me"
 * (30d vs 1d). Without it, this marker can't tell a remembered session from a
 * regular one and either logs the user out early or too late.
 */
export const storeTokens = (
  accessToken: string,
  expiresIn: number = 3600, // seconds
  refreshExpiresIn?: number // seconds
): void => {
  const now = Date.now();
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(TOKEN_EXPIRY_KEY, (now + expiresIn * 1000).toString());
  const refreshLifetimeMs =
    typeof refreshExpiresIn === 'number' ? refreshExpiresIn * 1000 : DEFAULT_REFRESH_LIFETIME_MS;
  localStorage.setItem(REFRESH_EXPIRY_KEY, (now + refreshLifetimeMs).toString());
};

/**
 * Get access token if still valid
 */
export const getAccessToken = (): string | null => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!token || !expiry) {
    return null;
  }

  // Check if token is expired
  if (Date.now() >= parseInt(expiry, 10)) {
    return null;
  }

  return token;
};

/**
 * One-time migration: sessions created before the httpOnly-cookie change hold
 * their refresh token in localStorage. Return it (and delete it) so the next
 * refresh call can send it in the body once — the response installs the cookie.
 */
export const consumeLegacyRefreshToken = (): string | null => {
  const token = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
  if (token) {
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  }
  return token;
};

/**
 * Check if access token is expired or will expire soon
 * Returns true if token will expire within the buffer time
 */
export const isTokenExpired = (bufferSeconds: number = 60): boolean => {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!expiry) {
    return true;
  }

  const expiryTime = parseInt(expiry, 10);
  const bufferTime = Date.now() + bufferSeconds * 1000;

  return bufferTime >= expiryTime;
};

/**
 * Check if the session (refresh token) is expired, per the local expiry marker.
 * The authoritative check happens server-side on the next refresh attempt.
 */
export const isRefreshTokenExpired = (): boolean => {
  const expiry = localStorage.getItem(REFRESH_EXPIRY_KEY);

  if (!expiry) {
    return true;
  }

  return Date.now() >= parseInt(expiry, 10);
};

/**
 * Get time until token expiry in seconds
 */
export const getTimeUntilExpiry = (): number => {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!expiry) {
    return 0;
  }

  const expiryTime = parseInt(expiry, 10);
  const now = Date.now();

  if (now >= expiryTime) {
    return 0;
  }

  return Math.floor((expiryTime - now) / 1000);
};

/**
 * Clear all authentication state (including the legacy refresh-token key).
 * The httpOnly cookie is cleared by the backend on logout/failed refresh.
 */
export const clearAllTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(REFRESH_EXPIRY_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
};

/**
 * Check if user is authenticated (valid access token or a live session marker)
 */
export const isAuthenticated = (): boolean => {
  return getAccessToken() !== null || !isRefreshTokenExpired();
};

/**
 * Parse JWT token (without verification - verification happens on backend)
 * Use this only to read public claims like expiry
 */
export const parseJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
};

/**
 * Get token expiry from JWT payload
 */
export const getTokenExpiryFromJWT = (token: string): number | null => {
  const payload = parseJWT(token);
  return payload?.exp ? payload.exp * 1000 : null; // Convert to milliseconds
};
